import { FC, useMemo } from 'react';

import MaterialIcon from 'components/atoms/MaterialIcon';
import { Button } from 'components/molecules/button';
import TableMenu from 'components/molecules/tableMenu';
import { FloatingTooltipText } from 'components/molecules/tooltipFloating';
import { canAutomatedIndexingTaskResume } from 'container/services/automatedIndexing/AutomatedIndexingRepo';
import styles from 'features/orderEntry/filesAndLogs/styles.module.scss';
import { isAutomatedIndexingTask } from 'pages/automatedIndexingDetails';
import { valueIsNumber } from 'utils/typeChecking';

import { getTooltip } from './config';
import {
  isAutomatedIndexingProps,
  isGeneralPayablesTask,
  isMaintenancePayablesTask,
  isOrderEntryTask,
  isRetryProps,
  isRetryTask,
  isTaskThatCanBeProcessed,
  TaskDetailsProps,
} from './types';

const TaskDetails: FC<TaskDetailsProps> = (props) => {
  const { task, viewFile, goToLogs, menuConfig } = props;
  const tooltip = useMemo(
    () =>
      !isRetryTask(task)
        ? getTooltip(task.status, task.errorMessage ?? '')
        : undefined,
    [task]
  );

  return (
    <>
      <div className={styles.task}>
        <div className={styles.iconContainer}>
          {tooltip && (
            <FloatingTooltipText title={tooltip.text} useDefaultWidthAndHeight>
              <MaterialIcon name={tooltip.name} alt="" color={tooltip.color} />
            </FloatingTooltipText>
          )}
        </div>

        <div className={styles.body}>
          <FloatingTooltipText
            title={`View ${task.name}`}
            useDefaultWidthAndHeight
          >
            <button className={styles.name} onClick={viewFile}>
              {task.name}
            </button>
          </FloatingTooltipText>
          <FloatingTooltipText
            title={`View logs for file`}
            useDefaultWidthAndHeight
          >
            <button onClick={goToLogs}>
              {new Date(task.updatedAt).toLocaleTimeString()}
            </button>
          </FloatingTooltipText>

          <p>
            {task.pages && valueIsNumber(task.pages)
              ? task.pages > 1
                ? `${task.pages} pages`
                : `1 page`
              : `No pages`}
          </p>

          {/* To show the Process Task button:
           (a) the link must be present
           (b) this must not be the Retry Task tab
           (c) the function to process task must be there
           (d) it must be an order entry, maintenance payables, or general payables task
           */}
          {task._links &&
            task._links['self'] &&
            task._links['lock'] &&
            !isRetryProps(props) &&
            !isRetryTask(task) &&
            (isOrderEntryTask(task) ||
              isMaintenancePayablesTask(task) ||
              isGeneralPayablesTask(task)) &&
            isTaskThatCanBeProcessed(props) && (
              <Button
                view={'Default'}
                label={'Process Task'}
                isWide
                classes={styles.ButtonSpacing}
                onClick={() => props.onProcessTaskClick(task)}
              />
            )}

          {isAutomatedIndexingProps(props) &&
            isAutomatedIndexingTask(task) &&
            canAutomatedIndexingTaskResume(task) && (
              <Button
                view={'Default'}
                label={'Resume Execution'}
                isWide
                classes={styles.ButtonSpacing}
                onClick={() => props.onResumeTrainingTaskClick(task)}
              />
            )}
        </div>

        <div>
          <TableMenu
            data={task}
            menuConfig={menuConfig}
            onClick={props.handleMenuItemClick}
          />
        </div>
      </div>
    </>
  );
};

export default TaskDetails;
